import { memo, useCallback, useMemo, useState } from 'react';

import { useT } from '@/app/i18n/client';
import CommonCalendar from '@/components/common/Calendar';
import type { ISlotItemDate } from '@/entities/slot/types';
import {
  utilDateDaysInMonth,
  utilDateDiffMonth,
  utilDateFirstDayOfMonth,
  utilDateMaxMonth,
  utilDateNextMonth,
  utilDatePrevMonth,
  utilDateThisMonth,
  utilDateIsPast,
} from '@/lib/date';
import { cn } from '@/lib/utils';

interface IProps {
  currentMonth: Date;
  selectedDate: Date | null;
  availableStartDate?: Date | string | null;
  availableEndDate?: Date | string | null;
  slotDateList?: ISlotItemDate[];
  handleDateClick: (date: Date) => void;
  handleChangeMonth: (date: Date) => (e: React.MouseEvent<HTMLButtonElement>) => void;
}
function ProductSelectDate({
  currentMonth,
  selectedDate,
  availableStartDate,
  availableEndDate,
  slotDateList,
  handleDateClick,
  handleChangeMonth,
}: IProps) {
  const { t, i18n } = useT('product');

  const [defaultMaxMonth] = useState<number>(13);

  const normalizeDate = useCallback((date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);

  const formatDateToString = useCallback((date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const availableDateSet = useMemo(() => {
    if (!slotDateList) return null;
    return new Set(slotDateList.map(item => formatDateToString(new Date(item.date))));
  }, [slotDateList, formatDateToString]);

  const normalizedSelectedDate = useMemo(() => {
    return selectedDate ? normalizeDate(selectedDate) : null;
  }, [normalizeDate, selectedDate]);

  const normalizedAvailableStartDate = useMemo(() => {
    if (!availableStartDate) return null;
    return normalizeDate(new Date(availableStartDate));
  }, [availableStartDate, normalizeDate]);

  const normalizedAvailableEndDate = useMemo(() => {
    if (!availableEndDate) return null;
    return normalizeDate(new Date(availableEndDate));
  }, [availableEndDate, normalizeDate]);

  const renderCalendar = useCallback((): React.JSX.Element[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = utilDateDaysInMonth(currentMonth);
    const firstDay = utilDateFirstDayOfMonth(currentMonth);
    const prevMonthDate = new Date(year, month - 1, 1);
    const prevMonthDays = utilDateDaysInMonth(prevMonthDate);
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const nextMonthDayCount = totalCells - firstDay - daysInMonth;

    const calendarCells: { date: Date; isCurrentMonth: boolean }[] = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      calendarCells.push({ date: new Date(year, month - 1, day), isCurrentMonth: false });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      calendarCells.push({ date: new Date(year, month, day), isCurrentMonth: true });
    }

    for (let day = 1; day <= nextMonthDayCount; day++) {
      calendarCells.push({ date: new Date(year, month + 1, day), isCurrentMonth: false });
    }

    return calendarCells.map(({ date, isCurrentMonth }) => {
      const normalizedDate = normalizeDate(date);
      const monthForPastCheck = new Date(
        normalizedDate.getFullYear(),
        normalizedDate.getMonth(),
        1,
      );
      const isSelected = normalizedSelectedDate?.getTime() === normalizedDate.getTime();
      const isPast = utilDateIsPast(monthForPastCheck, normalizedDate.getDate());
      const isBeforeAvailableStart =
        normalizedAvailableStartDate !== null && normalizedDate < normalizedAvailableStartDate;
      const isAfterAvailableEnd =
        normalizedAvailableEndDate !== null && normalizedDate > normalizedAvailableEndDate;
      const isSunday = normalizedDate.getDay() === 0;

      const isNotInSlotDateList =
        availableDateSet !== null && !availableDateSet.has(formatDateToString(normalizedDate));

      const isDisabled =
        isPast || isBeforeAvailableStart || isAfterAvailableEnd || isNotInSlotDateList;

      const getButtonClassName = (): string => {
        if (isSelected) return 'bg-[#0064FF] text-white';
        if (isDisabled) return 'cursor-not-allowed bg-[#F0F1F3] text-[#B0B0B0]';
        if (!isCurrentMonth) return 'text-gray-500';
        return `${isSunday ? 'text-red-500' : 'text-[#2E2E2E]'} hover:bg-[#F8F9FA]`;
      };

      return (
        <div key={normalizedDate.toISOString()} className='h-12 max-sm:h-8'>
          <button
            onClick={e => {
              e.stopPropagation();
              if (isDisabled) return;
              handleDateClick(normalizedDate);
            }}
            className={cn(
              'm-auto flex h-12 w-12 flex-1 items-center justify-center rounded-xl text-lg font-medium max-sm:h-7 max-sm:w-7 max-sm:text-sm',
              getButtonClassName(),
            )}
          >
            <span>{normalizedDate.getDate()}</span>
          </button>
        </div>
      );
    });
  }, [
    currentMonth,
    normalizeDate,
    normalizedAvailableEndDate,
    normalizedAvailableStartDate,
    normalizedSelectedDate,
    handleDateClick,
    availableDateSet,
    formatDateToString,
  ]);

  const isPrevMonthAvailable = useMemo((): boolean => {
    return utilDateDiffMonth(utilDatePrevMonth(currentMonth), utilDateThisMonth()) >= 0;
  }, [currentMonth]);
  const isNextMonthAvailable = useMemo((): boolean => {
    const nextMonth = utilDateNextMonth(currentMonth);
    const maxMonth = utilDateMaxMonth(defaultMaxMonth);

    return utilDateDiffMonth(nextMonth, maxMonth) < 0;
  }, [currentMonth, defaultMaxMonth]);

  return (
    <CommonCalendar
      title={t('selectDate')}
      buttonLabel={selectedDate?.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}
      handleChangeMonth={handleChangeMonth}
      currentMonth={currentMonth}
      renderCalendar={renderCalendar}
      isPrevMonthAvailable={isPrevMonthAvailable}
      isNextMonthAvailable={isNextMonthAvailable}
    />
  );
}

export default memo(ProductSelectDate);
